<template>
  <div 
    ref="containerRef"
    class="lazy-field-container"
    :class="{ 'is-loading': !isLoaded, 'is-loaded': isLoaded }"
  >
    <!-- Placeholder avant chargement -->
    <div 
      v-if="!isLoaded" 
      class="lazy-field-placeholder"
      :class="placeholderClass"
      @click="loadComponent"
    >
      <div class="placeholder-content">
        <i :class="placeholderIcon" class="placeholder-icon"></i>
        <p class="placeholder-text">{{ placeholderText }}</p>
        <div v-if="previewContent" class="placeholder-preview">
          <div class="preview-content">{{ previewContent }}</div>
        </div>
      </div>
    </div>

    <!-- Composant chargé -->
    <component
      v-if="isLoaded"
      :is="componentName"
      v-bind="componentProps"
      @update:model-value="$emit('update:modelValue', $event)"
      v-on="componentEvents"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref, computed, PropType, nextTick } from 'vue';
import BaseInputText from './BaseInputText.vue';
import BaseTextarea from './BaseTextarea.vue';
import BaseImage from './BaseImage.vue';
import BaseLink from './BaseLink.vue';

export default defineComponent({
  name: 'LazyFieldWrapper',

  components: {
    BaseInputText,
    BaseTextarea,
    BaseImage,
    BaseLink,
  },

  props: {
    // Nom du composant à charger
    componentName: {
      type: String,
      required: true,
    },

    // Props à passer au composant
    componentProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    // Events à écouter du composant
    componentEvents: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    // Mode de chargement
    loadMode: {
      type: String,
      required: false,
      default: 'auto',
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },

    // Seuil de visibilité
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },

    // Personnalisation du placeholder
    placeholderText: {
      type: String,
      required: false,
      default: 'Cliquer pour charger',
    },

    placeholderIcon: {
      type: String,
      required: false,
      default: 'fa fa-edit',
    },

    placeholderClass: {
      type: String,
      required: false,
      default: '',
    },

    // Contenu d'aperçu
    previewContent: {
      type: String,
      required: false,
      default: '',
    },

    // Option pour forcer le chargement immédiat
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    const loadComponent = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadComponent();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadComponent();
      } else if (props.loadMode === 'auto') {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    return {
      containerRef,
      isLoaded,
      loadComponent,
    };
  },
});
</script>

<style scoped lang="scss">
.lazy-field-container {
  min-height: 60px;
  transition: all 0.3s ease;

  &.is-loading {
    border: 1px dashed #e0e0e0;
    border-radius: 4px;
  }

  &.is-loaded {
    border: none;
  }
}

.lazy-field-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 0.75rem;
  cursor: pointer;
  background: #fafafa;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    border-color: #ccc;
  }

  .placeholder-content {
    text-align: center;
    max-width: 100%;
  }

  .placeholder-icon {
    font-size: 1.5rem;
    color: #999;
    margin-bottom: 0.25rem;
  }

  .placeholder-text {
    color: #666;
    font-size: 0.8rem;
    margin: 0 0 0.5rem 0;
    font-style: italic;
  }

  .placeholder-preview {
    max-width: 100%;
    margin-top: 0.25rem;
    
    .preview-content {
      background: white;
      border: 1px solid #ddd;
      border-radius: 3px;
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
      color: #555;
      text-align: left;
      max-height: 40px;
      overflow: hidden;
      line-height: 1.2;
    }
  }
}

// Placeholder spécifique pour les différents types de champs
.lazy-field-placeholder {
  &.textarea-placeholder {
    min-height: 80px;
    .placeholder-icon {
      font-size: 1.8rem;
    }
  }

  &.wysiwyg-placeholder {
    min-height: 120px;
    .placeholder-icon {
      font-size: 2rem;
    }
  }

  &.image-placeholder {
    min-height: 100px;
    .placeholder-icon {
      font-size: 2.5rem;
    }
  }

  &.link-placeholder {
    min-height: 50px;
    .placeholder-icon {
      font-size: 1.2rem;
    }
  }
}
</style>
